import { useEffect, useState } from 'react'
import { WellDrinkingWaterFieldsEdit } from '../../../../../types/form-types'
import { FormDelegate } from '../../../../../types/routing-types'
import ConfirmTransition from '../../../../components/confirm-transition'
import { ERROR_MESSAGES } from '../../../../components/errors/messages'
import WellDrinkingWaterEdit from '../../../../components/well-forms/drinking-water/drinking-water-edit'
import NotesWrapperEdit from '../../../../components/well-forms/notes/notes-wrapper-edit'
import WellEditButtons, { ActionProps } from '../../../../components/wells/well-edit-buttons'
import { useWellState } from '../../../../context/well-context'
import { useGetWellData, useSaveWellData } from '../../../../hooks/use-api'
import useEditForm from '../../../../hooks/use-edit-form'
import useToastMessage from '../../../../hooks/use-toast-message'
import { useWellId } from '../../../../hooks/use-well-id'
import { useNavigate } from 'react-router-dom'
import { RecordContent, RecordFooter, useTitleChange } from '@msaf/core-react'

export function EditWellDrinkingWater({ setIsDirty }: FormDelegate) {
  const {
    wellState: { isDraft },
  } = useWellState()
  useTitleChange(`${isDraft ? 'New' : 'Edit'} Well | Drinking Water`)
  const id = useWellId()
  const navigate = useNavigate()
  const url = `wells/${id}/drinking-water/edit`
  const continueUrl = `/wells/${id}/edit/aquifer`

  const stateQueryResult = useGetWellData(url)
  const saveMutationResult = useSaveWellData(url)

  const [isLoadingButtons, setIsLoadingButtons] = useState(true)

  const {
    fields,
    isSaveError,
    error,
    isSuccess,
    isLoading,
    isSaved,
    setValue,
    handleSave,
    addField,
    removeField,
    editField,
    validationErrors,
  } = useEditForm<WellDrinkingWaterFieldsEdit>({
    stateQueryResult,
    saveMutationResult,
    setIsDirty,
  })

  useToastMessage({
    errorMessage: validationErrors ? ERROR_MESSAGES.VALIDATION_ERROR : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: 'Save successful',
    error,
    isSuccess: isSaved,
    isError: isSaveError,
  })

  useEffect(() => {
    if (fields && isSuccess) {
      setIsLoadingButtons(false)
    }
  }, [fields, isSuccess])

  const actions: Array<ActionProps> = [
    {
      label: 'Save and continue',
      buttonStyle: 'primary',
      onClick: () =>
        handleSave(() => navigate(continueUrl, { state: { isProgrammaticTransition: true, prevFormSaved: true } })),
    },
    {
      label: 'Save',
      buttonStyle: 'primary',
      onClick: () => handleSave(),
    },
    {
      label: 'Return to view',
      buttonStyle: 'text-action',
      onClick: () => navigate(`/wells/${id}/view/drinking-water-supply`),
    },
  ]

  const notes = (
    <NotesWrapperEdit
      notes={fields.notes ?? []}
      isLoading={isLoading}
      addField={(note) => addField('notes', note, true)}
      removeField={(index: number) => removeField('notes', index)}
      editField={(note, idx) => editField('notes', note, idx)}
      setIsDirty={setIsDirty}
    />
  )

  return (
    <>
      <RecordContent>
        <WellDrinkingWaterEdit
          validationErrors={validationErrors ?? []}
          fields={fields as WellDrinkingWaterFieldsEdit}
          isLoading={isLoading}
          setValue={setValue}
        />
      </RecordContent>
      <RecordFooter notes={notes} areActionsFixed={true}>
        <WellEditButtons isLoading={isLoadingButtons} actions={actions} />
      </RecordFooter>
    </>
  )
}

export default function EditWellDrinkingWaterWrapped() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <EditWellDrinkingWater setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
